import { DEFAULT_FEATURE_FLAGS, FEATURE_FLAG_DESCRIPTIONS } from '@/config/featureFlags';
import featureFlagService from '@/services/featureFlags';

const state = {
  flags: { ...DEFAULT_FEATURE_FLAGS },
  isLoading: false
};

const mutations = {
  SET_FLAGS(state, flags) {
    state.flags = { ...flags };
  },
  SET_FLAG(state, { name, value, state: flagState }) {
    state.flags[name] = { value, state: flagState };
  },
  SET_LOADING(state, loading) {
    state.isLoading = loading;
  }
};

const actions = {
  /**
   * Initialize feature flags from service
   */
  async initializeFlags({ commit }) {
    commit('SET_LOADING', true);
    try {
      const flags = featureFlagService.getAllFlags();
      commit('SET_FLAGS', flags);
    } catch (error) {
      console.error('Failed to initialize feature flags:', error);
    } finally {
      commit('SET_LOADING', false);
    }
  },

  /**
   * Set a feature flag value
   */
  async setFlag({ commit }, { name, value }) {
    try {
      featureFlagService.setFlag(name, value);
      const newState = value ? 'on' : 'off';
      commit('SET_FLAG', { name, value, state: newState });
    } catch (error) {
      console.error('Failed to set feature flag:', error);
      throw error;
    }
  },

  /**
   * Reset all flags to defaults
   */
  async resetFlags({ commit }) {
    try {
      featureFlagService.resetToDefaults();
      const flags = featureFlagService.getAllFlags();
      commit('SET_FLAGS', flags);
    } catch (error) {
      console.error('Failed to reset feature flags:', error);
      throw error;
    }
  },

  /**
   * Force reset all flags and clear localStorage
   */
  async forceResetFlags({ commit }) {
    try {
      featureFlagService.forceResetToDefaults();
      const flags = featureFlagService.getAllFlags();
      commit('SET_FLAGS', flags);
    } catch (error) {
      console.error('Failed to force reset feature flags:', error);
      throw error;
    }
  }
};

const getters = {
  /**
   * Get all feature flags
   */
  getAllFlags: state => state.flags,

  /**
   * Check if a specific flag is enabled
   */
  isFlagEnabled: state => flagName => {
    const flag = state.flags[flagName];
    return flag ? flag.value : false;
  },

  /**
   * Get the state of a specific flag
   */
  getFlagState: state => flagName => {
    const flag = state.flags[flagName];
    return flag ? flag.state : 'disabled';
  },

  /**
   * Check if a flag is changeable
   */
  isFlagChangeable: state => flagName => {
    const flag = state.flags[flagName];
    return flag ? flag.state !== 'disabled' : false;
  },

  /**
   * Get flags grouped by category
   */
  getFlagsByCategory: state => {
    const categories = {};
    
    Object.keys(state.flags).forEach(flagName => {
      const description = FEATURE_FLAG_DESCRIPTIONS[flagName];
      const category = description?.category || 'Other';
      
      if (!categories[category]) {
        categories[category] = [];
      }
      
      const flag = state.flags[flagName];
      categories[category].push({
        name: flagName,
        value: flag.value,
        state: flag.state,
        changeable: flag.state !== 'disabled',
        title: description?.title || flagName,
        description: description?.description || ''
      });
    });
    
    return categories;
  },

  /**
   * Get loading state
   */
  isLoading: state => state.isLoading
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
};